<!-- Wrapper around PrimeVue's InputText.
	adds handling for translated descriptions

	If the prop "translatedDescription" is set, its value is shown in the input,
	the input is readonly and a link to translation page is shown
	The target is resolved automatically from the current route, but can be overridden via the "translationUrl" prop
	or disabled completely with the prop "disableTranslationUrl"

	When no translation is present the component renders the default InputText
-->
<template>
	<InputGroup v-if="isLocked" class="dex-input-text">
		<InputText v-bind="$attrs" :value="translatedDescription" :readonly="isReadonly" />
		<InputGroupAddon class="dex-input-text__addon">
			<RouterLink
				v-if="showTranslationLink"
				:to="resolvedTranslationUrl"
				class="dex-input-text__icon-link"
				v-tooltip.bottom="$t('Squeeze.System.TranslationFieldLockedLink')"
			>
				<i class="mdi mdi-translate" />
			</RouterLink>
			<i
				v-else
				class="mdi mdi-translate"
				v-tooltip.bottom="$t('Squeeze.System.TranslationFieldLocked')"
			/>
		</InputGroupAddon>
	</InputGroup>
	<InputText v-else v-bind="$attrs" :readonly="isReadonly" />
</template>

<script lang="ts">
import {computed, defineComponent, PropType, useAttrs} from "vue";
import {RouteLocationRaw, useRoute} from "vue-router";
import InputText from "primevue/inputtext";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";

export default defineComponent({
	name: "DexInputText",
	components: {
		InputText,
		InputGroup,
		InputGroupAddon,
	},
	inheritAttrs: false,
	props: {
		/** If set, a translation exists, the field is locked (read-only) and an info icon with a tooltip is shown. */
		translatedDescription: {
			type: String,
			default: null,
		},
		/** If true, the tooltip is only information and does not link anywhere. */
		disableTranslationUrl: {
			type: Boolean,
			default: false,
		},
		/**
		 * Explicit link target for the icon. If not set, it is resolved automatically from the current route.
		 * Accepts a path or a router location, e.g. {name: "LocatorTranslationsView"}. Needed whenever a form
		 * is rendered outside of its own route, where the automatic resolution cannot work.
		 */
		translationUrl: {
			type: [String, Object] as PropType<RouteLocationRaw>,
			default: null,
		},
		/**
		 * Translation key of this field. When set, it is passed to the translation page as the "focusKey"
		 * query parameter so the matching row is scrolled into view and briefly highlighted.
		 */
		translationKey: {
			type: String,
			default: null,
		},
	},
	setup(props) {
		const route = useRoute();
		const attrs = useAttrs() as Record<string, unknown>;

		/** A translation exists -> the field is locked. */
		const isLocked = computed(() => Boolean(props.translatedDescription));

		/** Read-only when locked or when the consumer explicitly requested read-only. */
		const isReadonly = computed(() => {
			const isReadonly = attrs.readonly === "" || attrs.readonly === true || attrs.readonly === "true";
			return isLocked.value || isReadonly;
		});

		/** Whether the icon should link to the translation page. */
		const showTranslationLink = computed(() => !props.disableTranslationUrl);

		/** Resolve the translation page path based on the current route when no explicit URL is given. */
		const resolvedTranslationPath = computed<RouteLocationRaw>(() => {
			if (props.translationUrl) {
				return props.translationUrl;
			}

			// Document classes (incl. their fields, groups, tables and columns):
			// the translations live in a dedicated tab of the document class.
			if (route.params.documentClassId) {
				return {
					name: "DocumentClassTranslationsSubView",
					params: {documentClassId: route.params.documentClassId},
				};
			}

			// Locators: one shared translation page for all locators.
			if (route.matched.some(record => record.path.includes("/locators"))) {
				return {name: "LocatorTranslationsView"};
			}

			// Batch classes: one shared translation page for all batch classes.
			if (route.params.batchClassId) {
				return {name: "BatchClassTranslationsView"};
			}

			// Master data: the translations page in a tab of the master data table page
			if (route.params.masterDataTableId) {
				return {
					name: "MasterDataView",
					params: { masterDataTableId: route.params.masterDataTableId },
				};
			}

			// Everything else - e.g. master data, whose translations are a plain tab of the
			// master data view and therefore not addressable via the URL: the global language
			// overview, where every translation can be edited as well.
			return {name: "LanguagesView"};
		});

		/**
		 * Router target for the translation icon. When a translation key is known, it is added as the
		 * "focusKey" query parameter so the translation table can scroll to and highlight the matching row.
		 */
		const resolvedTranslationUrl = computed<RouteLocationRaw>(() => {
			const target = resolvedTranslationPath.value;

			if (!props.translationKey) {
				return target;
			}

			if (typeof target === "string") {
				return {path: target, query: {focusKey: props.translationKey}};
			}

			return {...target, query: {...target.query, focusKey: props.translationKey}};
		});

		return {
			isLocked,
			isReadonly,
			showTranslationLink,
			resolvedTranslationUrl,
		};
	},
});
</script>

<style scoped>
.dex-input-text {
	width: 100%;
}

/* Keep the addon compact so it reads as an indicator, not a button. */
.dex-input-text__addon {
	color: var(--text-color-secondary, #6c757d);
	padding-top: 0;
	padding-bottom: 0;
}

.dex-input-text__addon .mdi {
	font-size: 1rem;
	line-height: 1;
}

.dex-input-text__icon-link {
	display: inline-flex;
	color: inherit;
	text-decoration: none;
}

.dex-input-text__icon-link:hover {
	color: var(--primary-color, #2196f3);
}
</style>
